/**
 * Ritual Bath Query Hooks
 *
 * React Query hooks for fetching ritual bath data.
 * Follows project conventions with query key helpers and standard options.
 */

import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import {
  ritualBathQueryKeys,
  ritualBathRepository,
} from "../repositories/ritualBath.repository";
import type {
  PaginatedRitualBathResponse,
  RitualBath,
  RitualBathListParams,
} from "../types/ritualBath.types";

// ============================================================================
// List Ritual Baths Query
// ============================================================================

interface UseRitualBathListQueryProps {
  params?: RitualBathListParams;
  enabled?: boolean;
}

/**
 * Query key helper for ritual bath list
 */
export const ritualBathListQueryKey = (params?: RitualBathListParams) =>
  ritualBathQueryKeys.list(params);

/**
 * Hook to fetch paginated list of ritual baths with filters
 */
export function useRitualBathListQuery({
  params,
  enabled = true,
}: UseRitualBathListQueryProps = {}) {
  return useQuery<PaginatedRitualBathResponse, Error>({
    queryKey: ritualBathListQueryKey(params),
    queryFn: () => ritualBathRepository.listRitualBaths(params),
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
    refetchOnMount: "always",
  });
}

/**
 * Infinite query hook for ritual bath list with filters
 */
export function useInfiniteRitualBathListQuery({
  params,
  enabled = true,
}: UseRitualBathListQueryProps = {}) {
  const limit = params?.limit || 20;
  // Remove page from params to avoid conflicts with pageParam
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { page, ...filterParams } = params || {};

  return useInfiniteQuery<PaginatedRitualBathResponse, Error>({
    queryKey: [...ritualBathListQueryKey(filterParams), "infinite"],
    queryFn: ({ pageParam = 1 }) =>
      ritualBathRepository.listRitualBaths({
        ...filterParams,
        page: pageParam as number,
        limit,
      }),
    enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.meta;
      const currentPage = typeof page === "string" ? parseInt(page, 10) : page;
      const maxPages =
        typeof totalPages === "string" ? parseInt(totalPages, 10) : totalPages;
      return currentPage < maxPages ? currentPage + 1 : undefined;
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: "always",
  });
}

// ============================================================================
// System Ritual Baths Query
// ============================================================================

/**
 * Query key helper for system ritual baths
 */
export const systemRitualBathsQueryKey = () => ritualBathQueryKeys.system();

/**
 * Hook to fetch all system ritual baths (origin = SYSTEM)
 */
export function useSystemRitualBathsQuery(enabled = true) {
  return useQuery<PaginatedRitualBathResponse, Error>({
    queryKey: systemRitualBathsQueryKey(),
    queryFn: () => ritualBathRepository.getSystemRitualBaths(),
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: "always",
  });
}

/**
 * Infinite query hook for system ritual baths
 */
export function useInfiniteSystemRitualBathsQuery(enabled = true) {
  return useInfiniteQuery<PaginatedRitualBathResponse, Error>({
    queryKey: [...systemRitualBathsQueryKey(), "infinite"],
    queryFn: ({ pageParam }) =>
      ritualBathRepository.listRitualBaths({
        origin: "SYSTEM",
        page: pageParam as number,
        limit: 20,
      }),
    enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.meta;
      const currentPage = typeof page === "string" ? parseInt(page, 10) : page;
      const maxPages =
        typeof totalPages === "string" ? parseInt(totalPages, 10) : totalPages;
      return currentPage < maxPages ? currentPage + 1 : undefined;
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: "always",
  });
}

// ============================================================================
// My Ritual Baths Query
// ============================================================================

/**
 * Query key helper for current user's ritual baths
 */
export const myRitualBathsQueryKey = () => ritualBathQueryKeys.my();

/**
 * Hook to fetch the current user's ritual baths
 */
export function useMyRitualBathsQuery(enabled = true) {
  return useQuery<PaginatedRitualBathResponse, Error>({
    queryKey: myRitualBathsQueryKey(),
    queryFn: () => ritualBathRepository.getMyRitualBaths(),
    enabled,
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: "always",
  });
}

/**
 * Infinite query hook for current user's ritual baths
 */
export function useInfiniteMyRitualBathsQuery(enabled = true) {
  return useInfiniteQuery<PaginatedRitualBathResponse, Error>({
    queryKey: [...myRitualBathsQueryKey(), "infinite"],
    queryFn: ({ pageParam }) =>
      ritualBathRepository.getMyRitualBaths({
        page: pageParam as number,
        limit: 20,
      }),
    enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.meta;
      const currentPage = typeof page === "string" ? parseInt(page, 10) : page;
      const maxPages =
        typeof totalPages === "string" ? parseInt(totalPages, 10) : totalPages;
      return currentPage < maxPages ? currentPage + 1 : undefined;
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: "always",
  });
}

// ============================================================================
// Ritual Bath By ID Query
// ============================================================================

interface UseRitualBathByIdQueryProps {
  id?: string;
  enabled?: boolean;
}

/**
 * Query key helper for single ritual bath
 */
export const ritualBathByIdQueryKey = (id: string) =>
  ritualBathQueryKeys.detail(id);

/**
 * Hook to fetch a single ritual bath by ID
 */
export function useRitualBathByIdQuery({
  id,
  enabled = true,
}: UseRitualBathByIdQueryProps) {
  return useQuery<RitualBath, Error>({
    queryKey: ritualBathByIdQueryKey(id || ""),
    queryFn: () => {
      if (!id) throw new Error("Ritual bath ID is required");
      return ritualBathRepository.getRitualBathById(id);
    },
    enabled: enabled && !!id,
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: "always",
  });
}

// ============================================================================
// Ritual Baths By Terreiro Query
// ============================================================================

interface UseRitualBathsByTerreiroQueryProps {
  terreiroId?: string;
  enabled?: boolean;
}

/**
 * Query key helper for terreiro ritual baths
 */
export const ritualBathsByTerreiroQueryKey = (terreiroId: string) =>
  ritualBathQueryKeys.byTerreiro(terreiroId);

/**
 * Hook to fetch ritual baths by terreiro ID
 */
export function useRitualBathsByTerreiroQuery({
  terreiroId,
  enabled = true,
}: UseRitualBathsByTerreiroQueryProps) {
  return useQuery<PaginatedRitualBathResponse, Error>({
    queryKey: ritualBathsByTerreiroQueryKey(terreiroId || ""),
    queryFn: () => {
      if (!terreiroId) throw new Error("Terreiro ID is required");
      return ritualBathRepository.getRitualBathsByTerreiro(terreiroId);
    },
    enabled: enabled && !!terreiroId,
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: "always",
  });
}

/**
 * Infinite query hook for terreiro ritual baths
 */
export function useInfiniteRitualBathsByTerreiroQuery({
  terreiroId,
  enabled = true,
}: UseRitualBathsByTerreiroQueryProps) {
  return useInfiniteQuery<PaginatedRitualBathResponse, Error>({
    queryKey: [...ritualBathsByTerreiroQueryKey(terreiroId || ""), "infinite"],
    queryFn: ({ pageParam }) => {
      if (!terreiroId) throw new Error("Terreiro ID is required");
      return ritualBathRepository.getRitualBathsByTerreiro(terreiroId, {
        page: pageParam as number,
        limit: 20,
      });
    },
    enabled: enabled && !!terreiroId,
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.meta;
      const currentPage = typeof page === "string" ? parseInt(page, 10) : page;
      const maxPages =
        typeof totalPages === "string" ? parseInt(totalPages, 10) : totalPages;
      return currentPage < maxPages ? currentPage + 1 : undefined;
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: "always",
  });
}

// ============================================================================
// Ritual Baths By Medium Query
// ============================================================================

interface UseRitualBathsByMediumQueryProps {
  mediumId?: string;
  enabled?: boolean;
}

/**
 * Query key helper for medium ritual baths
 */
export const ritualBathsByMediumQueryKey = (mediumId: string) =>
  ritualBathQueryKeys.byMedium(mediumId);

/**
 * Hook to fetch ritual baths by medium ID
 */
export function useRitualBathsByMediumQuery({
  mediumId,
  enabled = true,
}: UseRitualBathsByMediumQueryProps) {
  return useQuery<RitualBath[], Error>({
    queryKey: ritualBathsByMediumQueryKey(mediumId || ""),
    queryFn: () => {
      if (!mediumId) throw new Error("Medium ID is required");
      return ritualBathRepository.getRitualBathsByMedium(mediumId);
    },
    enabled: enabled && !!mediumId,
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    refetchOnMount: "always",
  });
}
